import { adminRoles } from '@/constants';
import { useRoles } from '@/hooks/useRoles';
import styles from '@/pages/Unauthorized/styles.module.css';
import { useAppStore } from '@/stores/app';
import Button from '@nautilusbio/nb-design-system/Button';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/unauthorized/')({
  component: UnauthorizedPage,
});

function UnauthorizedPage() {
  const { t } = useTranslation('general');
  const navigate = useNavigate();
  const router = useRouter();

  // Read referrerPath directly from sessionStorage for reliability
  const [referrerPath, setReferrerPath] = useState<string | null>(null);
  const selectedOrganization = useAppStore(s => s.selectedOrganization);
  const { hasAnyRole } = useRoles(selectedOrganization?.id);

  const clearReferrerPath = useCallback(() => {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('unauthorized_referrer');
    }
    setReferrerPath(null);
  }, []);

  // On mount, always read from sessionStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const path = sessionStorage.getItem('unauthorized_referrer');
      setReferrerPath(path);
    }
  }, []);

  // Effect: If user regains access to the referrerPath (e.g. /users), redirect them
  useEffect(() => {
    if (
      referrerPath &&
      router.state.location.pathname === '/unauthorized' &&
      selectedOrganization?.id &&
      hasAnyRole &&
      hasAnyRole(adminRoles)
    ) {
      clearReferrerPath();
      navigate({ to: referrerPath });
    }
  }, [referrerPath, router.state.location.pathname, selectedOrganization?.id, hasAnyRole, clearReferrerPath, navigate]);

  const handleGoHome = useCallback(() => {
    navigate({ to: '/studies' });
  }, [navigate]);

  return (
    <Stack
      alignItems="center"
      className={styles.unauthorizedContainer}
      fullWidth
      fullHeight
      gap={4}
      justifyContent="center"
      orientation="vertical"
    >
      <Text as="h1" className={styles.unauthorizedTitle}>
        {t('unauthorized.title')}
      </Text>

      <Text as="span" className={styles.unauthorizedMessage}>
        {t('unauthorized.message1')}
      </Text>

      <Text as="span" className={styles.unauthorizedMessage}>
        {t('unauthorized.message2')}
      </Text>

      {referrerPath && (
        <Text as="span" className={styles.unauthorizedMessage} data-testid="attempted-path">
          {t('unauthorized.attempted_path', { path: referrerPath, interpolation: { escapeValue: false } })}
        </Text>
      )}

      <Button
        aria-label={t('unauthorized.go_home')}
        data-testid={'go-to-home-btn'}
        onPress={handleGoHome}
        size="md"
        visual="primary"
      >
        {t('unauthorized.go_home')}
      </Button>
    </Stack>
  );
}
